/**
 * Collapse a raw location (pathname, optionally with a `?search`) into a
 * low-cardinality route label suitable for use as a metric dimension
 * (AXI-3875).
 *
 * The router is element-based (no loaders / declared `:param` patterns reach
 * this layer), so we normalize by structure instead: any path segment that
 * looks like an id — a number, a UUID, a long opaque/hex token, or a
 * URL-encoded URN (`urn:...` arrives percent-encoded) — becomes `:id`. Static
 * segments (`/plant`, `/equipment`, …) are kept verbatim.
 *
 * Several screens are driven entirely by query params (`/configure?type=rca`,
 * `/runs?type=&run=`, `/plant/processes?process=`, dashboards `?view=`). We
 * keep only an allowlist of bounded-enum query keys (`type`/`tab`/`view`) in
 * the label so those screen variants stay distinguishable, and drop everything
 * else (`run`/`process`/`node`/`id`/`folder` ids) so cardinality stays bounded
 * to roughly the number of distinct screens. Navigation *detection* still keys
 * on the full pathname+search (see RouteTelemetry) so a query-only transition
 * opens a fresh TTID/TTFD window even though both ends share this label.
 */

const MAX_SEGMENTS = 8
const MAX_SEGMENT_LEN = 24
const MAX_QUERY_VALUE_LEN = 32
const LOW_CARD_QUERY_KEYS = ['type', 'tab', 'view']
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const LONG_HEX_RE = /^[0-9a-f]{16,}$/i
const ALL_DIGITS_RE = /^\d+$/

function isDynamicSegment(segment: string): boolean {
  if (segment.length === 0) return false
  if (ALL_DIGITS_RE.test(segment)) return true
  if (UUID_RE.test(segment)) return true
  if (LONG_HEX_RE.test(segment)) return true
  // URNs and other id payloads reach the URL percent-encoded (`urn%3A...`).
  if (segment.includes('%')) return true
  let decoded = segment
  try {
    decoded = decodeURIComponent(segment)
  } catch {
    /* malformed escape — fall back to the raw segment */
  }
  if (decoded.startsWith('urn:') || decoded.includes(':')) return true
  // Anything unusually long is almost certainly an opaque id, not a screen name.
  if (segment.length > MAX_SEGMENT_LEN) return true
  return false
}

function normalizePath(rawPath: string): string {
  const segments = rawPath.split('/').filter((s) => s.length > 0)
  if (segments.length === 0) return '/'
  const normalized = segments
    .slice(0, MAX_SEGMENTS)
    .map((segment) => (isDynamicSegment(segment) ? ':id' : segment))
  return `/${normalized.join('/')}`
}

function normalizeQueryValue(value: string): string {
  if (value.length > MAX_QUERY_VALUE_LEN || isDynamicSegment(value)) return ':id'
  return value
}

/** Normalize `pathname` (or `pathname?search`) into a low-cardinality label. */
export function routeLabelFromPath(pathWithSearch: string): string {
  const qIndex = pathWithSearch.indexOf('?')
  const rawPath = qIndex === -1 ? pathWithSearch : pathWithSearch.slice(0, qIndex)
  const rawSearch = qIndex === -1 ? '' : pathWithSearch.slice(qIndex + 1)

  const pathLabel = normalizePath(rawPath)
  if (!rawSearch) return pathLabel

  const params = new URLSearchParams(rawSearch)
  const parts: string[] = []
  for (const key of LOW_CARD_QUERY_KEYS) {
    const value = params.get(key)
    if (value) parts.push(`${key}=${normalizeQueryValue(value)}`)
  }
  return parts.length > 0 ? `${pathLabel}?${parts.join('&')}` : pathLabel
}
